// src/api/services.ts
import apiClient from './axios.config';
import { TaskInfo, HubInfo, RoutingTaskSummaryInfo } from '../models/types';
import { RouteConfigRepresentation } from '../models/routeConfig';
import { UserInfoResponse } from '../models/userInfo';
import { GeoLocationCorrectionRequest, routingJobsApi, DepotResponse, RoutingJobResponse, TripEditRequest } from './routingJobsApi';
import { getTenantModelDefinition } from '../modules/tenantRegistry';
import { OrderBundle } from './wmService';

// Mock hubs data - used as a reference for generating samples
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mockHubsReference = [
  { hubId: 'hub-001', hubName: 'Downtown Distribution Center', location: 'Cityville' },
  { hubId: 'hub-002', hubName: 'North Warehouse', location: 'Northport' },
  { hubId: 'hub-003', hubName: 'East Logistics Hub', location: 'Easttown' },
  { hubId: 'hub-004', hubName: 'South Fulfillment Center', location: 'Southfield' },
  { hubId: 'hub-005', hubName: 'West Shipping Facility', location: 'Westview' }
];

// Mock user info data for development
const mockUserInfoData: UserInfoResponse = {
  userId: "HubId",
  config: {
    id: "cofigxyz",
    algoType: "BASIC",
    sourceLngLat: [72.556, 23.655],
    geoBarriers: [
      {
        id: "geobar1Id",
        name: "geobar1",
        geoBoundary: [
          [72.556, 23.655],
          [72.546, 23.635],
          [72.586, 23.625],
          [72.516, 23.685]
        ],
        allowedVehTypes: ["ACE"]
      },
      {
        id: "geobar2Id",
        name: "geobar2",
        geoBoundary: [
          [72.546, 23.615],
          [72.536, 23.625],
          [72.526, 23.635],
          [72.516, 23.645]
        ],
        allowedVehTypes: ["ACE"]
      }
    ]
  },
  drivers: [
    {
      name: "sachi",
      phNo: "778282828",
      vendorName: "tranz",
      vehicle: {
        vehicleId: "vehId",
        regNumber: "KA12MC9876",
        wtCapKg: 2000,
        maxRangeKm: 100,
        maxTp: 45,
        lbh: {
          lcm: 250,
          bcm: 180,
          hcm: 190
        },
        vehicleType: "ACE",
        contractType: "FIXED"
      }
    },
    {
      name: "bhalu",
      phNo: "777782828",
      vendorName: "tranz",
      vehicle: {
        vehicleId: "vehId1",
        regNumber: "KA12MC9879",
        wtCapKg: 2000,
        maxRangeKm: 100,
        maxTp: 45,
        lbh: {
          lcm: 250,
          bcm: 180,
          hcm: 190
        },
        vehicleType: "ACE",
        contractType: "FIXED"
      }
    }
  ],
  pastTps: [
    {
      tpId: "TPxzu01",
      tpName: "Coole Shoppe",
      tpAddress: "At house number, at vllage, at town at city",
      tpZipCode: "572301",
      geoLoc: [72.546, 23.655]
    },
    {
      tpId: "TPxzu02",
      tpName: "Hot Shoppe",
      tpAddress: "At house number, at vllage, at town at city",
      tpZipCode: "572301",
      geoLoc: [72.536, 23.655]
    },
    {
      tpId: "TPxzu03",
      tpName: "chill Shoppe",
      tpAddress: "At house number, at vllage, at town at city",
      tpZipCode: "572301",
      geoLoc: [72.526, 23.635]
    }
  ]
};

// Mock routing task input data (from TaskInputRepresentationJson.txt)
const mockRoutingTaskInput = {
  taskId: "Task-123",
  userId: "USER-XYN",
  tenant: "DEV",
  verificationStatus: "UNVERIFIED",
  touchPointsRepresentation: {
    count: 20,
    dispatchWeightKg: 200,
    loadPerTpKg: 10,
    touchPointGroupings: [
      {
        groupedBy: "PINCODE",
        groupKey: "343535",
        dispatchWeightKg: 200,
        count: 20,
        groupGeoBoundary: [
          [72.33, 22.55],[72.31, 22.53],[72.36, 22.52]
        ],
        groupCenter: [72.36, 22.52],
        touchPoints: [
          {
            tpId: "tp102020",
            tpName: "Shop Me",
            tpAddress: "Address at steet state and count ping",
            tpZipCode: "562800",
            geoLoc: {
              lngLat: {
                lng: 72.31,
                lat: 22.52
              }
            },
            items: [
              {
                id: "ITEM-243",
                unitWtGrams: 20,
                unitLbh: {
                  lcm: 200,
                  bcm: 3,
                  hcm: 2
                },
                unitPrice: 200,
                units: 2,
                action: "DELIVER",
                metaData: {}
              }
            ],
            openHours: [
              {
                start: 450,
                end: 740
              }
            ],
            metadata: {}
          }
        ]
      }
    ],
    config: {
      id: "USER_CONFIG-1232",
      algoType: "BASIC",
      sourceLngLat: [72.36, 22.52],
      geoBarriers: [
        {
          id: "Default",
          name: "Default",
          geoBoundary: [
            [72.33, 22.55],[72.31, 22.53],[72.36, 22.52]
          ],
          allowedVehTypes: [
            ""
          ],
          type: "INCLUDE"
        }
      ]
    }
  },
  drivers: [
    {
      name: "shoemaker",
      phNo: "880908903",
      vendorName: "Ven-1",
      vehicle: {
        vehicleId: "BOlw-1",
        regNumber: "KFHHKS0-1",
        wtCapKg: 2000,
        maxRangeKm: 200,
        maxTp: 30,
        lbh: {
          lcm: 250,
          bcm: 200,
          hcm: 180
        },
        vehicleType: "BOLERO",
        contractType: "FIXED"
      }
    }
  ]
};

export const hubService = {
  // Get hubs for user
  getHubsForUser: async (userId: string): Promise<HubInfo[]> => {
    console.log('[hubService] Getting hubs for user:', userId);
    const depots = await routingJobsApi.listDepots();
    return depots.map(mapDepotToHubInfo);
  },
};

export const taskSummaryService = {
  // Get task summaries for hub
  getTasksForHub: async (hubId: string): Promise<RoutingTaskSummaryInfo[]> => {
    console.log('[taskSummaryService.getTasksForHub] Getting tasks for hub:', hubId);
    const jobs = await routingJobsApi.listTasks(hubId);
    return jobs.map(mapJobToTaskSummary);
  },

  getTaskForHub: async (depotId: string, jobId: string): Promise<RoutingTaskSummaryInfo> => {
    console.log('[taskSummaryService.getTaskForHub] Refreshing task:', { depotId, jobId });
    const job = await routingJobsApi.getJob(depotId, jobId);
    return mapJobToTaskSummary(job);
  },
};

const mapDepotToHubInfo = (depot: DepotResponse): HubInfo => ({
  hubId: depot.id,
  hubName: depot.name,
  location: depot.configFilesMap?.depot_snapshot,
  tenantId: depot.tenantId,
  modelType: depot.modelType,
  active: depot.active,
  configFilesMap: depot.configFilesMap ?? {},
});

export const mapJobToTaskSummary = (job: RoutingJobResponse): RoutingTaskSummaryInfo => ({
  taskId: job.id,
  depotId: job.depotId,
  tenantId: job.depotSnapshot.tenantId,
  modelType: job.depotSnapshot.modelType,
  createdBy: job.createdBy,
  status: job.status,
  createdAt: new Date(job.createdAtEpoch),
  updatedAt: new Date(job.updatedAtEpoch),
  metrics: job.metrics ?? {},
});

export interface TaskDetailPayload {
  taskInfo: TaskInfo;
  orders: OrderBundle[];
}

export interface ApplyTripEditParams {
  depotId: string;
  taskId: string;
  request: TripEditRequest;
}

export interface ApplyGeoLocationCorrectionParams {
  depotId: string;
  taskId: string;
  request: GeoLocationCorrectionRequest;
}

export interface GeoLocationCorrectionResult {
  taskInfo: TaskInfo;
  message: string;
}

export const tripPlanningService = {
  // Get task info
  getTaskInfo: async (
    depotId: string,
    taskId: string,
    outputMode: 'edited' | 'original' = 'edited'
  ): Promise<TaskDetailPayload> => {
    console.log('[tripPlanningService] Fetching task info', { depotId, taskId });
    const tripsView = await routingJobsApi.getView(depotId, taskId, 'edit_trips', outputMode);
    const tenantDefinition = getTenantModelDefinition(
      tripsView.summary?.tenantId,
      tripsView.summary?.modelType
    );
    const supportViews = tenantDefinition.key === 'gemba:gemba_slotted'
      ? {
          verify_vehicles: await routingJobsApi
            .getView(depotId, taskId, 'verify_vehicles', outputMode)
            .catch(() => undefined),
        }
      : undefined;
    const taskInfo = tenantDefinition.adaptTaskInfo({
      depotId,
      jobId: taskId,
      summaryView: tripsView,
      tripsView,
      supportViews,
    });
    return { taskInfo, orders: [] };
  },

  // Move touch points between trips
  moveToTouchPoints: async (
    fromTripId: string,
    touchPointIds: string[],
    toTripId: string
  ): Promise<TaskInfo> => {
    console.log('moveToTouchPoints not supported yet', { fromTripId, touchPointIds, toTripId });
    throw new Error('Trip edits are not supported in this release.');
  },

  applyTripEdit: async ({
    depotId,
    taskId,
    request,
  }: ApplyTripEditParams): Promise<TaskInfo> => {
    const response = await routingJobsApi.editTrip(depotId, taskId, request);
    const tenantDefinition = getTenantModelDefinition(
      response.job.depotSnapshot?.tenantId,
      response.job.depotSnapshot?.modelType
    );
    const viewsById = Object.fromEntries((response.views ?? []).map((view) => [view.viewId, view]));
    const tripsView = viewsById.edit_trips;
    const summaryView = viewsById.edit_summary ?? tripsView;
    if (!summaryView || !tripsView) {
      throw new Error('Edit response did not include edit views');
    }
    return tenantDefinition.adaptTaskInfo({
      depotId,
      jobId: taskId,
      summaryView,
      tripsView,
      supportViews: viewsById,
    });
  },

  applyGeoLocationCorrection: async ({
    depotId,
    taskId,
    request,
  }: ApplyGeoLocationCorrectionParams): Promise<GeoLocationCorrectionResult> => {
    const response = await routingJobsApi.correctGeoLocation(depotId, taskId, request);
    const tenantDefinition = getTenantModelDefinition(
      response.job.depotSnapshot?.tenantId,
      response.job.depotSnapshot?.modelType
    );
    const viewsById = Object.fromEntries((response.views ?? []).map((view) => [view.viewId, view]));
    const tripsView = viewsById.edit_trips;
    const summaryView = viewsById.edit_summary ?? tripsView;
    if (!summaryView || !tripsView) {
      throw new Error('Correction response did not include edit views');
    }
    const supportViews = tenantDefinition.key === 'gemba:gemba_slotted'
      ? {
          ...viewsById,
          verify_vehicles: viewsById.verify_vehicles ?? await routingJobsApi
            .getView(depotId, taskId, 'verify_vehicles', 'edited')
            .catch(() => undefined),
        }
      : viewsById;
    return {
      taskInfo: tenantDefinition.adaptTaskInfo({
        depotId,
        jobId: taskId,
        summaryView,
        tripsView,
        supportViews,
      }),
      message: response.message,
    };
  },

  // Mock routing task input data (from TaskInputRepresentationJson.txt)
  getRoutingTaskInput: async (taskId: string) => {
    // Simulate API call with delay
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockRoutingTaskInput), 600);
    });
  },
  verifyTouchPoints: async (taskId: string) => {
    // Simulate API call to update status to TOUCH_POINT_VERIFIED
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ ...mockRoutingTaskInput, verificationStatus: 'TOUCH_POINT_VERIFIED' });
      }, 600);
    });
  },
  runAlgo: async (taskId: string) => {
    // Simulate API call to update status to VERIFIED
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ ...mockRoutingTaskInput, verificationStatus: 'VERIFIED' });
      }, 600);
    });
  },
};

// Mock route config data
const mockRouteConfig: RouteConfigRepresentation = {
  userId: "UUID-f4556",
  algoType: "BASIC",
  sourceLngLat: [72.551, 22.038],
  geoBarriers: [
    {
      id: "gb-narrow-bridge",
      name: "Narrow Bridge Region",
      geoBoundary: [
        [72.5605, 22.0421],
        [72.5570, 22.0360],
        [72.5520, 22.0355],
        [72.5485, 22.0405]
      ],
      allowedVehTypes: [],
      excludedVehTypes: ["LARGE_TRUCK"],
      excludedHours: []
    },
    {
      id: "gb-daytime-only",
      name: "Daytime Service Region (10:00–18:00)",
      geoBoundary: [
        [72.5455, 22.0480],
        [72.5400, 22.0415],
        [72.5435, 22.0365],
        [72.5495, 22.0390]
      ],
      allowedVehTypes: [],
      excludedVehTypes: [],
      // Excluded hours from 18:00 to 10:00 to allow service only 10:00–18:00
      excludedHours: [[1800, 1000]]
    }
  ],
  operationTime: [600, 1800],
  transactionTimeMinutes: 5
};

export const routeConfigService = {
  // Get route configuration for a user
  getRouteConfig: async (userId: string): Promise<RouteConfigRepresentation> => {
    console.log(`[routeConfigService][${process.env.NODE_ENV}] Getting route config for user: ${userId}`);

    
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'production' ) {
      // Return mock data in development mode
      return new Promise((resolve) => {
        console.log('[routeConfigService] Development mode - returning mock route config');
        setTimeout(() => resolve({...mockRouteConfig, userId}), 500);
      });
    }
    
    // In production mode, use real API
    const response = await apiClient.get(`/config/user-id/${userId}`);
    return response.data;
  },
  
  // Update route configuration
  updateRouteConfig: async (config: RouteConfigRepresentation): Promise<RouteConfigRepresentation> => {
    console.log('[routeConfigService] Updating route config:', config);
    
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'production') {
      // Simulate API call in development mode
      return new Promise((resolve) => {
        console.log('[routeConfigService] Development mode - simulating config update');
        // Return the updated config (in a real app, this would come from the server)
        setTimeout(() => resolve(config), 800);
      });
    }
    
    // In production mode, use real API
    const response = await apiClient.put(`/config/user-id/${config.userId}`, config);
    return response.data;
  }
};

export const userInfoService = {
  getUserInfo: async (hubId: string): Promise<UserInfoResponse> => {
    console.log('[userInfoService] Getting user info for hub:', hubId);
    
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'production') {
      // Return mock data in development mode
      return new Promise((resolve) => {
        console.log('[userInfoService] Development mode - returning mock user info');
        setTimeout(() => resolve({...mockUserInfoData, userId: hubId}), 800);
      });
    }
    
    // In production, use real API
    try {
      const response = await apiClient.get(`/user/${hubId}/info`);
      return response.data;
    } catch (error) {
      console.error('[userInfoService] Error fetching user info:', error);
      // Fallback to mock data even in production if API fails
      return {...mockUserInfoData, userId: hubId};
    }
  }
};
